/**
 * Night rates, holiday rates, and the calendar that decides what a holiday is.
 *
 * A rule either carries prices of its own — the tariff's six fields, every one set
 * separately, which is how a Bulgarian night tariff is written — or adds a percentage and an
 * amount on top of the tariff's. Prices first: a new rule opens with them, pre-filled from
 * the tariff in force so a firm changes the figures that differ rather than typing all six.
 *
 * Unlike a tariff, these are edited in place rather than versioned. The fares they produced
 * are stored on their orders and are never recomputed, so changing a night rate cannot
 * change what anybody was charged last week — which is what makes editing safe here and
 * unsafe one screen over. That is also why this page has no save bar: each rule and each
 * date is upserted on its own, and a page-level Save would be pretending to a transaction
 * the server does not have.
 *
 * A band that ends before it starts wraps midnight. That is not a mistake to be corrected,
 * it is how every firm writes a night rate down, and the form says so rather than refusing
 * 22:00 to 06:00.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  daySet,
  everyDay,
  newId,
  settingsApi,
  surchargeKinds,
  surchargeModes,
  surchargePrices,
  type Holiday,
  type Surcharge,
  type SurchargeKind,
  type SurchargeMode,
  type Tariff,
} from '../api';
import { refusal } from '../errors';
import { useTenantFormats } from '../../shell/formats';
import {
  Banner,
  Button,
  Chip,
  ChipGroup,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  FieldErrors,
  Icon,
  Input,
  Measured,
  PageHeader,
  Pill,
  Section,
  Select,
  Sub,
  Switch,
  Table,
  Td,
  Th,
  Tr,
  type FieldProblem,
} from '../../ui';

const emptyRule: Omit<Surcharge, 'id'> = {
  tariffCode: null,
  kind: 'TimeBand',
  mode: 'Rates',
  code: '',
  displayName: '',
  startsAt: '22:00',
  endsAt: '06:00',
  days: everyDay,
  percent: 0,
  amount: 0,
  baseFare: null,
  perKilometre: null,
  perMinute: null,
  perWaitingMinute: null,
  freeWaitingMinutes: null,
  minimumFare: null,
  isActive: true,
};

/**
 * A rule's prices copied from the tariff in force for every class — or left as they are when
 * it has prices already, or when there is no tariff to copy.
 *
 * A night tariff differs from the day's in some figures and not others; starting from the
 * day's means typing only the ones that differ, and never saving a forgotten box as zero.
 * Applied to a new rule and to an uplift switched to prices of its own alike.
 */
function withDayPrices<R extends Omit<Surcharge, 'id'>>(rule: R, tariffs: readonly Tariff[]): R {
  const day =
    tariffs.find((tariff) => tariff.effectiveTo === null && tariff.vehicleCategoryCode === null) ??
    tariffs.find((tariff) => tariff.effectiveTo === null);

  return day === undefined || rule.baseFare !== null
    ? rule
    : {
        ...rule,
        baseFare: day.baseFare,
        perKilometre: day.perKilometre,
        perMinute: day.perMinute,
        perWaitingMinute: day.perWaitingMinute,
        freeWaitingMinutes: day.freeWaitingMinutes,
        minimumFare: day.minimumFare,
      };
}

const emptyHoliday: Omit<Holiday, 'id'> = {
  date: new Date().toISOString().slice(0, 10),
  name: '',
  recurringAnnually: true,
  isActive: true,
};

/** The day flags in the order the API numbers them, Sunday being the low bit. */
const DAYS = Object.entries(daySet);

/** How long a "Saved" banner stays before the screen goes quiet again. */
const SAID = 4000;

export function SurchargesPage() {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [rules, setRules] = useState<Surcharge[] | null>(null);
  const [holidays, setHolidays] = useState<Holiday[] | null>(null);

  /** The tariffs, to pre-fill a new rule's prices from the one in force. */
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [rule, setRule] = useState<Omit<Surcharge, 'id'> & { id?: string }>(emptyRule);
  const [holiday, setHoliday] = useState<Omit<Holiday, 'id'> & { id?: string }>(emptyHoliday);

  /** Which form is open in a dialog, if either. */
  const [editing, setEditing] = useState<'rule' | 'holiday' | null>(null);

  /** Which section just saved something, for the few seconds its banner is up. */
  const [saved, setSaved] = useState<'rule' | 'holiday' | null>(null);
  const [error, setError] = useState<FieldProblem | null>(null);

  const load = useCallback(async () => {
    try {
      setRules(await settingsApi.surcharges());
      setHolidays(await settingsApi.holidays());
      setTariffs(await settingsApi.tariffs());
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  useEffect(() => {
    if (saved === null) return undefined;

    const timer = setTimeout(() => setSaved(null), SAID);

    return () => clearTimeout(timer);
  }, [saved]);

  async function saveRule() {
    setError(null);

    try {
      const { id, ...body } = rule;

      // The other mode's figures are not sent: the server clears them anyway, and a rule
      // switched to an uplift must not keep a night tariff nobody can see.
      const sent =
        body.mode === 'Rates'
          ? { ...body, percent: 0, amount: 0 }
          : {
              ...body,
              baseFare: null,
              perKilometre: null,
              perMinute: null,
              perWaitingMinute: null,
              freeWaitingMinutes: null,
              minimumFare: null,
            };

      await settingsApi.saveSurcharge(id ?? newId(), sent);

      // The dialog closes only once the server has taken it. A dialog that closes on submit
      // takes the half-typed rule with it, and the failure is announced to an empty screen.
      setRule(emptyRule);
      setEditing(null);
      setSaved('rule');
      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotSave'));
    }
  }

  async function saveHoliday() {
    setError(null);

    try {
      const { id, ...body } = holiday;

      await settingsApi.saveHoliday(id ?? newId(), body);
      setHoliday(emptyHoliday);
      setEditing(null);
      setSaved('holiday');
      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotSave'));
    }
  }

  /** What the confirmation is about, or nothing while it is shut. */
  const [removing, setRemoving] = useState<{ what: 'rule' | 'holiday'; id: string; name: string } | null>(null);

  async function remove(what: 'rule' | 'holiday', id: string) {
    setError(null);
    setRemoving(null);

    try {
      await (what === 'rule'
        ? settingsApi.deleteSurcharge(id)
        : settingsApi.deleteHoliday(id));

      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotSave'));
    }
  }

  /** The days a band runs on, as words rather than as the number the API keeps. */
  function onDays(days: number): string {
    return days === everyDay
      ? t('surcharges.allDays')
      : DAYS.filter(([, bit]) => (days & bit) !== 0)
          .map(([name]) => t(`surcharges.day.${name}`))
          .join(' · ');
  }

  /**
   * What the rule charges: its own start and kilometre, or a percentage, a fixed amount, or
   * both, exactly as the fare shows it.
   */
  function uplift(existing: Surcharge): string {
    if (existing.mode === 'Rates') {
      return t('surcharges.ownPrices', {
        base: formats.money(existing.baseFare ?? 0),
        perKilometre: formats.money(existing.perKilometre ?? 0),
      });
    }

    const parts: string[] = [];

    if (existing.percent > 0) parts.push(t('surcharges.percent', { percent: existing.percent }));
    if (existing.amount > 0) {
      parts.push(t('surcharges.amount', { amount: formats.money(existing.amount) }));
    }

    return parts.length === 0 ? '—' : parts.join(' + ');
  }

  return (
    <FieldErrors problem={error} onEdit={() => setError(null)}>
      <>
        <PageHeader
          title={t('nav.settings.surcharges')}
          description={t('nav.settingsAbout.surcharges')}
        />

        {error !== null && editing === null && <Banner tone="danger">{error.message}</Banner>}

        <Section
          title={t('surcharges.rules')}
          description={t('surcharges.rulesHelp')}
          actions={
            <Button
              size="sm"
              onClick={() => {
                setRule(withDayPrices(emptyRule, tariffs));
                setEditing('rule');
              }}
            >
              <Icon name="plus" small />
              {t('surcharges.addRule')}
            </Button>
          }
        >
          {saved === 'rule' && (
            <div className="px-5 pt-3">
              <Banner tone="ok">{t('settings.saved')}</Banner>
            </div>
          )}

          {rules === null ? (
            <EmptyState>{t('app.loading')}</EmptyState>
          ) : rules.length === 0 ? (
            <EmptyState>{t('surcharges.noRules')}</EmptyState>
          ) : (
            <Table>
              <thead>
                <Tr>
                  <Th>{t('surcharges.name')}</Th>
                  <Th>{t('surcharges.kindLabel')}</Th>
                  <Th numeric>{t('surcharges.uplift')}</Th>
                  <Th>{t('surcharges.active')}</Th>
                  <Th />
                </Tr>
              </thead>
              <tbody>
                {rules.map((existing) => (
                  <Tr key={existing.id}>
                    <Td wrap>
                      {existing.displayName}
                      <Sub block>{existing.code}</Sub>
                    </Td>
                    <Td wrap>
                      {existing.kind === 'TimeBand'
                        ? t('surcharges.band', { from: existing.startsAt, to: existing.endsAt })
                        : t('surcharges.onHolidays')}
                      {existing.kind === 'TimeBand' && <Sub block>{onDays(existing.days)}</Sub>}
                    </Td>
                    <Td numeric>{uplift(existing)}</Td>
                    <Td>
                      {existing.isActive ? (
                        <Pill tone="ok" soft>
                          {t('surcharges.active')}
                        </Pill>
                      ) : (
                        <Pill>{t('surcharges.inactive')}</Pill>
                      )}
                    </Td>
                    <Td numeric>
                      <span className="inline-flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => {
                            setRule(existing);
                            setEditing('rule');
                          }}
                        >
                          {t('surcharges.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() =>
                            setRemoving({ what: 'rule', id: existing.id, name: existing.displayName })
                          }
                        >
                          {t('surcharges.remove')}
                        </Button>
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Section>

        <Section
          title={t('surcharges.calendar')}
          description={t('surcharges.calendarHelp')}
          actions={
            <Button
              size="sm"
              onClick={() => {
                setHoliday(emptyHoliday);
                setEditing('holiday');
              }}
            >
              <Icon name="plus" small />
              {t('surcharges.addHoliday')}
            </Button>
          }
        >
          {saved === 'holiday' && (
            <div className="px-5 pt-3">
              <Banner tone="ok">{t('settings.saved')}</Banner>
            </div>
          )}

          {holidays === null ? (
            <EmptyState>{t('app.loading')}</EmptyState>
          ) : holidays.length === 0 ? (
            <EmptyState>{t('surcharges.noHolidays')}</EmptyState>
          ) : (
            <Table>
              <thead>
                <Tr>
                  <Th>{t('surcharges.holidayName')}</Th>
                  <Th>{t('surcharges.holidayDate')}</Th>
                  <Th>{t('surcharges.active')}</Th>
                  <Th />
                </Tr>
              </thead>
              <tbody>
                {holidays.map((existing) => (
                  <Tr key={existing.id}>
                    <Td wrap>{existing.name}</Td>
                    <Td>
                      {formats.day(existing.date)}
                      <Sub block>
                        {existing.recurringAnnually
                          ? t('surcharges.recurring')
                          : existing.date.slice(0, 4)}
                      </Sub>
                    </Td>
                    <Td>
                      {existing.isActive ? (
                        <Pill tone="ok" soft>
                          {t('surcharges.active')}
                        </Pill>
                      ) : (
                        <Pill>{t('surcharges.inactive')}</Pill>
                      )}
                    </Td>
                    <Td numeric>
                      <span className="inline-flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => {
                            setHoliday(existing);
                            setEditing('holiday');
                          }}
                        >
                          {t('surcharges.edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() =>
                            setRemoving({ what: 'holiday', id: existing.id, name: existing.name })
                          }
                        >
                          {t('surcharges.remove')}
                        </Button>
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Section>

        <ConfirmDialog
          open={removing !== null}
          title={t(
            removing?.what === 'holiday'
              ? 'surcharges.confirmRemoveHolidayTitle'
              : 'surcharges.confirmRemoveRuleTitle',
          )}
          confirm={t('surcharges.remove')}
          cancel={t('surcharges.cancelEdit')}
          onConfirm={() => {
            if (removing !== null) void remove(removing.what, removing.id);
          }}
          onClose={() => setRemoving(null)}
        >
          {removing !== null &&
            t(
              removing.what === 'holiday'
                ? 'surcharges.confirmRemoveHoliday'
                : 'surcharges.confirmRemoveRule',
              { name: removing.name },
            )}
        </ConfirmDialog>

        <Dialog
          open={editing === 'rule'}
          wide
          title={rule.id === undefined ? t('surcharges.addRule') : t('surcharges.editRule')}
          onClose={() => {
            setEditing(null);
            setRule(emptyRule);
          }}
          actions={
            <>
              <Button
                variant="quiet"
                onClick={() => {
                  setEditing(null);
                  setRule(emptyRule);
                }}
              >
                {t('surcharges.cancelEdit')}
              </Button>
              <Button variant="primary" type="submit" form="surcharge-rule">
                {t('surcharges.saveRule')}
              </Button>
            </>
          }
        >
          <form
            id="surcharge-rule"
            className="flex flex-col gap-3.5"
            onSubmit={(event) => {
              event.preventDefault();
              void saveRule();
            }}
          >
            {error !== null && <Banner tone="danger">{error.message}</Banner>}

            <Field id="kind" label={t('surcharges.kindLabel')} help={t('surcharges.kindLabelHelp')}>
              <Select
                value={rule.kind}
                onChange={(event) => setRule({ ...rule, kind: event.target.value as SurchargeKind })}
              >
                {surchargeKinds.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`surcharges.kind.${kind}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field id="code" label={t('surcharges.code')} help={t('surcharges.codeHelp')}>
              <Input
                value={rule.code}
                onChange={(event) => setRule({ ...rule, code: event.target.value })}
                required
              />
            </Field>

            <Field id="displayName" label={t('surcharges.name')} help={t('surcharges.nameHelp')}>
              <Input
                value={rule.displayName}
                onChange={(event) => setRule({ ...rule, displayName: event.target.value })}
                required
              />
            </Field>

            {rule.kind === 'TimeBand' && (
              <>
                <Field also={['startsAt', 'endsAt']} label={t('surcharges.hours')} help={t('surcharges.hoursHelp')}>
                  <span className="flex items-center gap-2">
                    <Input
                      id="startsAt"
                      type="time"
                      aria-label={t('surcharges.hours')}
                      value={rule.startsAt}
                      onChange={(event) => setRule({ ...rule, startsAt: event.target.value })}
                    />
                    <Input
                      id="endsAt"
                      type="time"
                      aria-label={t('surcharges.until')}
                      value={rule.endsAt}
                      onChange={(event) => setRule({ ...rule, endsAt: event.target.value })}
                    />
                  </span>
                </Field>

                <Field id="days" label={t('surcharges.days')}>
                  <ChipGroup label={t('surcharges.days')}>
                    {DAYS.map(([name, bit]) => (
                      <Chip
                        key={name}
                        current={(rule.days & bit) !== 0}
                        onClick={() =>
                          setRule({
                            ...rule,
                            days: (rule.days & bit) !== 0 ? rule.days & ~bit : rule.days | bit,
                          })
                        }
                      >
                        {t(`surcharges.day.${name}`)}
                      </Chip>
                    ))}
                  </ChipGroup>
                </Field>
              </>
            )}

            <Field id="mode" label={t('surcharges.modeLabel')} help={t('surcharges.modeHelp')}>
              <ChipGroup label={t('surcharges.modeLabel')}>
                {surchargeModes.map((mode) => (
                  <Chip
                    key={mode}
                    current={rule.mode === mode}
                    onClick={() => {
                      const next = { ...rule, mode: mode satisfies SurchargeMode };

                      setRule(mode === 'Rates' ? withDayPrices(next, tariffs) : next);
                    }}
                  >
                    {t(`surcharges.mode.${mode}`)}
                  </Chip>
                ))}
              </ChipGroup>
            </Field>

            {rule.mode === 'Rates' ? (
              <div className="grid grid-cols-2 gap-4">
                {surchargePrices.map((field) => (
                  <Price
                    key={field}
                    field={field}
                    unit={field === 'freeWaitingMinutes' ? t('settings.unitMinutes') : formats.currency}
                    value={rule[field]}
                    onChange={(value) => setRule({ ...rule, [field]: value })}
                  />
                ))}
              </div>
            ) : (
              <>
                <Field id="percent" label={t('surcharges.percentField')} help={t('surcharges.percentFieldHelp')}>
                  <Measured
                    unit="%"
                    aria-label={t('surcharges.percentField')}
                    type="number"
                    min={0}
                    max={500}
                    step={0.5}
                    value={rule.percent}
                    onChange={(event) => setRule({ ...rule, percent: event.target.valueAsNumber })}
                  />
                </Field>

                <Field id="amount" label={t('surcharges.amountField')} help={t('surcharges.amountFieldHelp')}>
                  <Measured
                    unit={formats.currency}
                    aria-label={t('surcharges.amountField')}
                    type="number"
                    min={0}
                    step={0.01}
                    value={rule.amount}
                    onChange={(event) => setRule({ ...rule, amount: event.target.valueAsNumber })}
                  />
                </Field>
              </>
            )}

            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-body font-medium">{t('surcharges.active')}</div>
                <div className="text-secondary text-ink-3 mt-0.5">{t('surcharges.activeHelp')}</div>
              </div>
              <Switch
                checked={rule.isActive}
                onChange={(next) => setRule({ ...rule, isActive: next })}
                label={t('surcharges.active')}
              />
            </div>
          </form>
        </Dialog>

        <Dialog
          open={editing === 'holiday'}
          title={holiday.id === undefined ? t('surcharges.addHoliday') : t('surcharges.editHoliday')}
          onClose={() => {
            setEditing(null);
            setHoliday(emptyHoliday);
          }}
          actions={
            <>
              <Button
                variant="quiet"
                onClick={() => {
                  setEditing(null);
                  setHoliday(emptyHoliday);
                }}
              >
                {t('surcharges.cancelEdit')}
              </Button>
              <Button variant="primary" type="submit" form="surcharge-holiday">
                {t('surcharges.saveHoliday')}
              </Button>
            </>
          }
        >
          <form
            id="surcharge-holiday"
            className="flex flex-col gap-3.5"
            onSubmit={(event) => {
              event.preventDefault();
              void saveHoliday();
            }}
          >
            {error !== null && <Banner tone="danger">{error.message}</Banner>}

            <Field id="name" label={t('surcharges.holidayName')} help={t('surcharges.holidayNameHelp')}>
              <Input
                value={holiday.name}
                onChange={(event) => setHoliday({ ...holiday, name: event.target.value })}
                required
              />
            </Field>

            <Field id="date" label={t('surcharges.holidayDate')} help={t('surcharges.holidayDateHelp')}>
              <Input
                type="date"
                value={holiday.date}
                onChange={(event) => setHoliday({ ...holiday, date: event.target.value })}
                required
              />
            </Field>

            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-body font-medium">{t('surcharges.recurring')}</div>
                <div className="text-secondary text-ink-3 mt-0.5">
                  {t('surcharges.recurringHelp')}
                </div>
              </div>
              <Switch
                checked={holiday.recurringAnnually}
                onChange={(next) => setHoliday({ ...holiday, recurringAnnually: next })}
                label={t('surcharges.recurring')}
              />
            </div>
          </form>
        </Dialog>
      </>
    </FieldErrors>
  );
}

/**
 * One of a rule's own prices, labelled and explained exactly as the tariff's same field is.
 *
 * The tariff's words rather than new ones: a night kilometre is a kilometre, and two
 * explanations of one field would drift apart.
 */
function Price({
  field,
  unit,
  value,
  onChange,
}: {
  field: (typeof surchargePrices)[number];
  unit: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  const { t } = useTranslation();
  const whole = field === 'freeWaitingMinutes';

  return (
    <Field id={field} label={t(`tariffs.${field}`)} help={t(`tariffs.${field}Help`)}>
      <Measured
        unit={unit}
        aria-label={t(`tariffs.${field}`)}
        type="number"
        required
        min={0}
        max={whole ? 60 : undefined}
        step={whole ? 1 : field.startsWith('per') ? 0.0001 : 0.01}
        value={value === null || Number.isNaN(value) ? '' : value}
        onChange={(event) =>
          onChange(Number.isNaN(event.target.valueAsNumber) ? null : event.target.valueAsNumber)
        }
      />
    </Field>
  );
}
